import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  builtInRegistry,
  FormRendererProvider,
  PropertyRenderer,
} from "@/lib/data-driven-forms/renderer"
import { FormProperties } from "@/lib/data-driven-forms/schema"
import { TFunction } from "i18next"
import { useState } from "react"
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import { RHFDirtyDraftRegistration } from "@/components/shared/dirty-draft-navigation"
import { useNavigate } from "@/lib/page-rendering"

type Fact = {
  field_key: string
  active: boolean
  term_value_keys?: string[]
  ordinal_lower_key?: string
  ordinal_upper_key?: string
  number_lower_bound?: number
  number_upper_bound?: number
  boolean_value?: boolean
  integer_value?: number
  text_value?: string
  comparison_mode?: string
}

type Content = {
  definition_key: string
  version: number
  enabled: boolean
  order: number
  facts: Fact[]
}

type Field = {
  key: string
  kind: string
  student_fact_key?: string
  comparator?: string
  label_key: string
  description_key?: string
  help_text_key?: string
  display_order: number
  option_set_key?: string
}

type DefinitionVersion = {
  version: number
  fields: Field[]
  option_sets: Array<{
    key: string
    options: Array<{ key: string; label_key: string }>
  }>
}

type Definition = {
  key: string
  label_key: string
  description_key?: string
  help_text_key?: string
  published_version: number
  display_order: number
  versions: DefinitionVersion[]
}

type Category = {
  key: string
  label_key: string
  description_key?: string
  help_text_key?: string
  display_order: number
  definitions: Definition[]
}

export type DataDrivenSpecialityFormData = {
  name: string | null
  description: string
  eligibility_mode: "selective" | "descriptive"
  contents: Content[]
  catalogue: Category[]
}

type WizardStage = "details" | "categories" | "configuration" | "review"
type CatalogueTranslator = (key: string) => string

function useCatalogueTranslation(): CatalogueTranslator {
  const { t, i18n } = useTranslation("speciality_catalogue" as never)
  const { t: tSpecialities } = useTranslation("specialities")
  const catalogueI18n = i18n as unknown as {
    exists: (key: string, options: { ns: string }) => boolean
  }
  const translate = t as unknown as CatalogueTranslator

  return (key) =>
    catalogueI18n.exists(key, { ns: "speciality_catalogue" })
      ? translate(key)
      : tSpecialities("wizard.catalogue_content_unavailable")
}

const fieldValue = (field: Field): Omit<Fact, "field_key" | "active"> => {
  switch (field.kind) {
    case "taxonomy_multi_select":
    case "option_multi_select":
      return { term_value_keys: [] }
    case "ordinal_range":
      return { ordinal_lower_key: "", ordinal_upper_key: "" }
    case "number_range":
      return { number_lower_bound: undefined, number_upper_bound: undefined }
    case "boolean":
      return { boolean_value: false }
    case "integer":
      return { integer_value: undefined }
    default:
      return { text_value: "" }
  }
}

function defaultContents(form: DataDrivenSpecialityFormData): Content[] {
  const existing = new Map(
    form.contents.map((content) => [
      `${content.definition_key}:${content.version}`,
      content,
    ]),
  )

  return orderedCategories(form.catalogue).flatMap((category) =>
    orderedDefinitions(category.definitions).map((definition) => {
      const version = publishedVersion(definition)
      const content = existing.get(`${definition.key}:${version.version}`)

      return (
        content ?? {
          definition_key: definition.key,
          version: version.version,
          enabled: false,
          order: definition.display_order,
          facts: orderedFields(version.fields).map((field) => ({
            field_key: field.key,
            active: false,
            ...fieldValue(field),
          })),
        }
      )
    }),
  )
}

const orderedCategories = (categories: Category[]) =>
  [...categories].sort((a, b) => a.display_order - b.display_order)
const orderedDefinitions = (definitions: Definition[]) =>
  [...definitions].sort((a, b) => a.display_order - b.display_order)
const orderedFields = (fields: Field[]) =>
  [...fields].sort((a, b) => a.display_order - b.display_order)

function publishedVersion(definition: Definition) {
  const version = definition.versions.find(
    ({ version }) => version === definition.published_version,
  )
  if (!version)
    throw new Error(`Missing published version for ${definition.key}`)

  return version
}

function contentIndex(
  data: DataDrivenSpecialityFormData,
  definition: Definition,
) {
  const version = publishedVersion(definition)
  const index = data.contents.findIndex(
    (content) =>
      content.definition_key === definition.key &&
      content.version === version.version,
  )
  if (index < 0) throw new Error(`Missing content for ${definition.key}`)

  return index
}

function fieldProperty(
  field: Field,
  contentIndex: number,
  fieldIndex: number,
  version: DefinitionVersion,
  tCatalogue: CatalogueTranslator,
): FormProperties {
  const name = `contents.${contentIndex}.facts.${fieldIndex}`
  const optionSet = version.option_sets.find(
    ({ key }) => key === field.option_set_key,
  )
  const useOptions = () =>
    (optionSet?.options ?? []).map((option) => ({
      label: tCatalogue(option.label_key),
      value: option.key,
    }))

  switch (field.kind) {
    case "taxonomy_multi_select":
    case "option_multi_select":
      return {
        type: "field:combobox",
        name: `${name}.term_value_keys`,
        label: field.label_key,
        comboboxType: "multi",
        useOptions,
      } as FormProperties
    case "ordinal_range":
      return {
        type: "field:custom:value-range",
        name,
        properties: [
          {
            type: "field:combobox",
            name: "ordinal_lower_key",
            label: field.label_key,
            comboboxType: "single",
            useOptions,
          },
          {
            type: "field:combobox",
            name: "ordinal_upper_key",
            label: field.label_key,
            comboboxType: "single",
            useOptions,
          },
        ],
      } as FormProperties
    case "number_range":
      return {
        type: "field:custom:value-range",
        name,
        properties: [
          {
            type: "field:input",
            name: "number_lower_bound",
            label: field.label_key,
            inputType: "number",
          },
          {
            type: "field:input",
            name: "number_upper_bound",
            label: field.label_key,
            inputType: "number",
          },
        ],
      } as FormProperties
    case "boolean":
      return {
        type: "field:custom:toggle",
        name: `${name}.boolean_value`,
        label: field.label_key,
        value: true,
        isArray: false,
      } as FormProperties
    case "integer":
      return {
        type: "field:input",
        name: `${name}.integer_value`,
        label: field.label_key,
        inputType: "number",
      } as FormProperties
    default:
      return {
        type: "field:textarea",
        name: `${name}.text_value`,
        label: field.label_key,
      } as FormProperties
  }
}

function authoredPayload(
  data: DataDrivenSpecialityFormData,
): Omit<DataDrivenSpecialityFormData, "catalogue"> {
  const { catalogue: _catalogue, ...speciality } = data

  return {
    ...speciality,
    contents: data.contents.map((content) => ({
      ...content,
      enabled: Boolean(content.enabled),
      facts: content.enabled
        ? content.facts.map((fact) => ({
            ...fact,
            active: factIsActive(fact),
          }))
        : [],
    })),
  }
}

function factIsActive(fact: Fact) {
  return Object.entries(fact).some(
    ([key, value]) =>
      key !== "field_key" &&
      key !== "active" &&
      value !== "" &&
      value !== undefined &&
      value !== null &&
      (!Array.isArray(value) || value.length > 0),
  )
}

function hasActiveEvaluableCriterion(data: DataDrivenSpecialityFormData) {
  return data.contents.some((content) => {
    if (!content.enabled) return false

    const definition = data.catalogue
      .flatMap((category) => category.definitions)
      .find((candidate) => candidate.key === content.definition_key)
    const version = definition && publishedVersion(definition)
    return version?.fields.some((field) => {
      const fact = content.facts.find(
        (candidate) => candidate.field_key === field.key,
      )
      return Boolean(
        fact &&
        factIsActive(fact) &&
        field.student_fact_key &&
        field.comparator,
      )
    })
  })
}

export function isDataDrivenSpecialityFormData(
  value: unknown,
): value is DataDrivenSpecialityFormData {
  if (!value || typeof value !== "object") return false

  const form = value as Partial<DataDrivenSpecialityFormData>
  return (
    (typeof form.name === "string" || form.name === null) &&
    typeof form.description === "string" &&
    Array.isArray(form.contents) &&
    Array.isArray(form.catalogue)
  )
}

export function DataDrivenSpecialityForm({
  form,
  listingProfileId,
}: {
  form: DataDrivenSpecialityFormData
  listingProfileId?: string
}) {
  const navigate = useNavigate()
  const methods = useForm<DataDrivenSpecialityFormData>({
    defaultValues: {
      ...form,
      name: form.name ?? "",
      contents: defaultContents(form),
    },
  })
  const { t } = useTranslation("specialities")
  const tCatalogue = useCatalogueTranslation()
  const [stage, setStage] = useState<WizardStage>("details")
  const [activeCategoryKey, setActiveCategoryKey] = useState<string>()
  const [activeDefinitionKey, setActiveDefinitionKey] = useState<string>()
  const contents = useWatch({ control: methods.control, name: "contents" })
  const categories = orderedCategories(form.catalogue)
  const activeCategory = categories.find(({ key }) => key === activeCategoryKey)
  const activeDefinition = activeCategory?.definitions.find(
    ({ key }) => key === activeDefinitionKey,
  )

  const configure = (category: Category, definition: Definition) => {
    const index = contentIndex(methods.getValues(), definition)
    methods.setValue(`contents.${index}.enabled`, true)
    setActiveCategoryKey(category.key)
    setActiveDefinitionKey(definition.key)
    setStage("configuration")
  }

  const selected = (definition: Definition) => {
    const index = contentIndex(methods.getValues(), definition)
    return Boolean(contents[index]?.enabled)
  }

  return (
    <FormRendererProvider
      tFunc={tCatalogue as unknown as TFunction}
      registry={builtInRegistry}
    >
      <FormProvider {...methods}>
        <RHFDirtyDraftRegistration />
        <form
          id="data-driven-speciality-form"
          data-testid="data-driven-speciality-form"
          onSubmit={methods.handleSubmit((data) => {
            if (
              data.eligibility_mode === "selective" &&
              !hasActiveEvaluableCriterion(data)
            ) {
              methods.setError("root", {
                message: t("wizard.selective_criterion_required"),
              })
              return
            }
            const payload = authoredPayload(data)
            if (listingProfileId)
              navigate({
                href: ".",
                method: "post",
                data: {
                  listing_profile_id: listingProfileId,
                  speciality: payload,
                },
              })
            else
              navigate({
                href: ".",
                method: "patch",
                data: { speciality: payload },
              })
          })}
          className="flex flex-col gap-5"
        >
          {stage === "details" && (
            <Details
              onContinue={async () => {
                if (
                  await methods.trigger([
                    "name",
                    "description",
                    "eligibility_mode",
                  ])
                )
                  setStage("categories")
              }}
            />
          )}
          {stage === "categories" && (
            <CategoryBrowser
              categories={categories}
              activeCategory={activeCategory}
              selected={selected}
              onSelectCategory={setActiveCategoryKey}
              onConfigure={configure}
              onToggle={(definition) => {
                const index = contentIndex(methods.getValues(), definition)
                methods.setValue(
                  `contents.${index}.enabled`,
                  !selected(definition),
                )
              }}
              onReview={() => setStage("review")}
            />
          )}
          {stage === "configuration" && activeDefinition && (
            <DefinitionConfiguration
              definition={activeDefinition}
              contentIndex={contentIndex(methods.getValues(), activeDefinition)}
              onBack={() => setStage("categories")}
            />
          )}
          {stage === "review" && (
            <Review
              categories={categories}
              selected={selected}
              onBack={() => setStage("categories")}
              onEdit={configure}
              saveLabel={listingProfileId ? t("form.create") : t("form.update")}
              errorMessage={methods.formState.errors.root?.message}
            />
          )}
        </form>
      </FormProvider>
    </FormRendererProvider>
  )
}

function Details({ onContinue }: { onContinue: () => void }) {
  const { register } = useFormContext<DataDrivenSpecialityFormData>()
  const { t } = useTranslation("specialities")

  return (
    <>
      <label className="space-y-2">
        <span>{t("form.name")}</span>
        <Input {...register("name")} />
      </label>
      <label className="space-y-2">
        <span>{t("form.description")}</span>
        <Textarea {...register("description")} />
      </label>
      <fieldset className="space-y-2">
        <legend>{t("wizard.eligibility_mode")}</legend>
        <label>
          <input
            {...register("eligibility_mode")}
            type="radio"
            value="selective"
          />{" "}
          {t("wizard.selective")}
        </label>
        <label>
          <input
            {...register("eligibility_mode")}
            type="radio"
            value="descriptive"
          />{" "}
          {t("wizard.descriptive")}
        </label>
      </fieldset>
      <Button type="button" onClick={onContinue}>
        {t("wizard.continue")}
      </Button>
    </>
  )
}

function CategoryBrowser({
  categories,
  activeCategory,
  selected,
  onSelectCategory,
  onConfigure,
  onToggle,
  onReview,
}: {
  categories: Category[]
  activeCategory?: Category
  selected: (definition: Definition) => boolean
  onSelectCategory: (key: string) => void
  onConfigure: (category: Category, definition: Definition) => void
  onToggle: (definition: Definition) => void
  onReview: () => void
}) {
  const { t } = useTranslation("specialities")
  const tCatalogue = useCatalogueTranslation()
  return (
    <>
      <h2>{t("wizard.categories")}</h2>
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <Button
            key={category.key}
            type="button"
            variant={
              activeCategory?.key === category.key ? "default" : "outline"
            }
            onClick={() => onSelectCategory(category.key)}
          >
            {tCatalogue(category.label_key)}
          </Button>
        ))}
      </div>
      {activeCategory && (
        <div className="space-y-3">
          {orderedDefinitions(activeCategory.definitions).map((definition) => (
            <div key={definition.key} className="flex items-center gap-2">
              <span>{tCatalogue(definition.label_key)}</span>
              {selected(definition) && <span>{t("wizard.selected")}</span>}
              <Button
                type="button"
                onClick={() => onConfigure(activeCategory, definition)}
              >
                {selected(definition)
                  ? t("wizard.edit")
                  : t("wizard.configure")}
              </Button>
              {selected(definition) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onToggle(definition)}
                >
                  {t("wizard.deselect")}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <Button type="button" onClick={onReview}>
        {t("wizard.review")}
      </Button>
    </>
  )
}

function DefinitionConfiguration({
  definition,
  contentIndex,
  onBack,
}: {
  definition: Definition
  contentIndex: number
  onBack: () => void
}) {
  const { t } = useTranslation("specialities")
  const tCatalogue = useCatalogueTranslation()
  const version = publishedVersion(definition)
  return (
    <>
      <h2>{tCatalogue(definition.label_key)}</h2>
      {orderedFields(version.fields).map((field, fieldIndex) => (
        <PropertyRenderer
          key={field.key}
          formProperty={fieldProperty(
            field,
            contentIndex,
            fieldIndex,
            version,
            tCatalogue,
          )}
        />
      ))}
      <Button type="button" onClick={onBack}>
        {t("wizard.back_to_categories")}
      </Button>
    </>
  )
}

function Review({
  categories,
  selected,
  onBack,
  onEdit,
  saveLabel,
  errorMessage,
}: {
  categories: Category[]
  selected: (definition: Definition) => boolean
  onBack: () => void
  onEdit: (category: Category, definition: Definition) => void
  saveLabel: string
  errorMessage?: string
}) {
  const { t } = useTranslation("specialities")
  const tCatalogue = useCatalogueTranslation()
  return (
    <>
      <h2>{t("wizard.review")}</h2>
      {errorMessage && <p role="alert">{errorMessage}</p>}
      {categories.map((category) => {
        const definitions = orderedDefinitions(category.definitions).filter(
          selected,
        )
        if (!definitions.length) return null
        return (
          <section key={category.key}>
            <h3>{tCatalogue(category.label_key)}</h3>
            {definitions.map((definition) => (
              <Button
                key={definition.key}
                type="button"
                variant="outline"
                onClick={() => onEdit(category, definition)}
              >
                {tCatalogue(definition.label_key)}
              </Button>
            ))}
          </section>
        )
      })}
      <Button type="button" variant="outline" onClick={onBack}>
        {t("wizard.back_to_categories")}
      </Button>
      <Button type="submit">{saveLabel}</Button>
    </>
  )
}
